<template>
  <div v-if="items != null" class="History">
    <header class="History__masthead">
      <div class="History__topline">
        <div class="History__title-block">
          <v-app-bar-nav-icon class="History__nav-toggle" @click="showDrawer()" />
          <div>
            <span class="zp-overline">{{ $t('projectStream') }}</span>
            <h1 class="History__title">{{ $t('dashboard2') }}</h1>
            <p class="History__subtitle">{{ $t('dashboardSubtitle') }}</p>
          </div>
        </div>

        <div class="History__actions">
          <span class="History__updated zp-tabular">
            {{ $t('dashboardUpdated') }} {{ (latestTask ? latestTask.start : null) | formatDate }}
          </span>
          <button class="History__action-btn" type="button" @click="loadItems()">
            <ZpIcon name="refresh-cw" :size="16" />
            {{ $t('dashboardRefresh') }}
          </button>
          <router-link
            class="History__primary-action"
            :to="`/project/${projectId}/templates`"
          >
            <ZpIcon name="play" :size="16" />
            {{ $t('dashboardOpenTemplates') }}
          </router-link>
        </div>
      </div>
    </header>

    <div class="History__menu-row">
      <DashboardMenu
        class="History__menu"
        :project-id="projectId"
        :project-type="projectType"
        :can-update-project="can(USER_PERMISSIONS.updateProject)"
      />
    </div>

    <div class="History__metrics">
      <article
        v-for="card in metricCards"
        :key="card.key"
        class="History__metric"
        :class="`History__metric--${card.variant}`"
      >
        <div class="History__metric-icon">
          <ZpIcon :name="card.icon" :size="18" />
        </div>
        <div class="History__metric-body">
          <span class="History__metric-label">{{ card.label }}</span>
          <strong class="History__metric-value zp-tabular">{{ card.count }}</strong>
          <span class="History__metric-note">{{ card.note }}</span>
        </div>
      </article>
    </div>

    <section class="History__readiness">
      <div class="History__readiness-title">
        <span class="zp-overline">{{ $t('dashboardReadiness') }}</span>
      </div>
      <div
        v-for="signal in readinessSignals"
        :key="signal.key"
        class="History__signal"
      >
        <span class="History__signal-dot" :class="`History__signal-dot--${signal.variant}`" />
        <span class="History__signal-label">{{ signal.label }}</span>
        <strong class="History__signal-value zp-tabular">{{ signal.value }}</strong>
      </div>
    </section>

    <div class="History__workspace">
      <section class="History__stream-panel">
        <div class="History__panel-head">
          <div>
            <span class="zp-overline">{{ $t('dashboardTaskStream') }}</span>
            <h2>{{ $t('dashboardRecentRuns') }}</h2>
          </div>
          <span class="History__panel-count zp-tabular">
            {{ pageRangeStart }}–{{ pageRangeEnd }} / {{ items.length }}
          </span>
        </div>

        <div class="History__table-wrap">
          <table class="History__table" v-if="visibleItems.length > 0">
            <thead>
              <tr>
                <th class="History__col-status">{{ $t('status') }}</th>
                <th class="History__col-id">#</th>
                <th class="History__col-template">{{ $t('task2') }}</th>
                <th class="History__col-version">{{ $t('version') }}</th>
                <th class="History__col-user">{{ $t('user') }}</th>
                <th class="History__col-time">{{ $t('start') }}</th>
                <th class="History__col-duration">{{ $t('duration') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in visibleItems"
                :key="item.id"
                class="History__row"
                @click="openTask(item.id)"
              >
                <td class="History__col-status">
                  <TaskStatus :status="item.status" />
                </td>
                <td class="History__col-id zp-tabular">
                  <router-link
                    class="History__id-link"
                    :to="taskRoute(item.id)"
                    @click.native.stop
                  >{{ formatId(item.id) }}</router-link>
                </td>
                <td class="History__col-template">
                  <div class="History__template">
                    <v-icon class="History__app-icon" small>{{ getAppIcon(item.tpl_app) }}</v-icon>
                    <router-link
                      class="History__template-link"
                      :to="`/project/${item.project_id}/templates/${item.template_id}`"
                      @click.native.stop
                    >
                      {{ item.tpl_alias }}
                    </router-link>
                  </div>
                  <div v-if="item.message || item.commit_message" class="History__sub">
                    <ZpIcon
                      class="History__sub-icon"
                      :name="item.message ? 'message-square' : 'git-fork'"
                      :size="13"
                    />
                    <span>{{ item.message || item.commit_message }}</span>
                  </div>
                </td>
                <td class="History__col-version zp-tabular">
                  <TaskLink
                    v-if="item.tpl_type !== ''"
                    :disabled="item.tpl_type === 'build'"
                    :status="item.status"
                    :task-id="item.tpl_type === 'build' ? item.id : (item.build_task || {}).id"
                    :label="item.tpl_type === 'build'
                      ? item.version
                      : (item.build_task || {}).version"
                    :tooltip="item.tpl_type === 'build'
                      ? item.message
                      : (item.build_task || {}).message"
                  />
                  <span v-else class="History__muted">—</span>
                </td>
                <td class="History__col-user">
                  <span v-if="item.user_name" class="History__user">{{ item.user_name }}</span>
                  <span v-else class="History__muted">—</span>
                </td>
                <td class="History__col-time zp-tabular">{{ item.start | formatDate }}</td>
                <td class="History__col-duration zp-tabular">
                  <span>{{ [item.start, item.end] | formatMilliseconds }}</span>
                  <span class="History__duration-bar">
                    <span :style="{ width: durationPercent(item) + '%' }" />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-else class="History__empty">
            <span class="zp-overline">{{ $t('noFlightsLogged') }}</span>
            <p>{{ $t('noTasksInProjectYet') }}</p>
          </div>

          <div v-if="pageCount > 1" class="History__pager">
            <span class="zp-overline History__pager-range">
              {{ pageRangeStart }}–{{ pageRangeEnd }} / {{ items.length }}
            </span>
            <div class="History__pager-actions">
              <button
                class="History__pager-btn"
                :disabled="page === 1"
                @click="page = 1"
                type="button"
              >«</button>
              <button
                class="History__pager-btn"
                :disabled="page === 1"
                @click="page -= 1"
                type="button"
              >‹</button>
              <span class="History__pager-pos zp-tabular">{{ page }} / {{ pageCount }}</span>
              <button
                class="History__pager-btn"
                :disabled="page === pageCount"
                @click="page += 1"
                type="button"
              >›</button>
              <button
                class="History__pager-btn"
                :disabled="page === pageCount"
                @click="page = pageCount"
                type="button"
              >»</button>
            </div>
          </div>
        </div>
      </section>

      <aside class="History__ops">
        <section class="History__ops-card History__ops-card--danger">
          <span class="zp-overline">{{ $t('dashboardLastFailure') }}</span>
          <template v-if="lastFailure">
            <button class="History__ops-open" type="button" @click="openTask(lastFailure.id)">
              <span>
                <strong>{{ lastFailure.tpl_alias }}</strong>
                <small class="zp-tabular">
                  {{ formatId(lastFailure.id) }} · {{ lastFailure.start | formatDate }}
                </small>
              </span>
              <ZpIcon name="chevron-right" :size="16" />
            </button>
            <p class="History__ops-message">
              {{ lastFailure.message || lastFailure.commit_message || $t('dashboardNoMessage') }}
            </p>
          </template>
          <p v-else class="History__ops-empty">{{ $t('dashboardNoFailures') }}</p>
        </section>

        <section class="History__ops-card History__ops-card--success">
          <span class="zp-overline">{{ $t('dashboardLastSuccess') }}</span>
          <template v-if="lastSuccess">
            <button class="History__ops-open" type="button" @click="openTask(lastSuccess.id)">
              <span>
                <strong>{{ lastSuccess.tpl_alias }}</strong>
                <small class="zp-tabular">
                  {{ formatId(lastSuccess.id) }} ·
                  {{ [lastSuccess.start, lastSuccess.end] | formatMilliseconds }}
                </small>
              </span>
              <ZpIcon name="chevron-right" :size="16" />
            </button>
          </template>
          <p v-else class="History__ops-empty">{{ $t('dashboardNoSuccesses') }}</p>
        </section>

        <section class="History__ops-card">
          <div class="History__ops-head">
            <span class="zp-overline">{{ $t('dashboardTopTemplates') }}</span>
            <span class="History__ops-window">{{ $t('dashboardRecentWindow') }}</span>
          </div>
          <div v-if="topTemplates.length > 0" class="History__top-list">
            <div v-for="tpl in topTemplates" :key="tpl.key" class="History__top-item">
              <span class="History__top-name">{{ tpl.name }}</span>
              <span class="History__top-count zp-tabular">{{ tpl.count }}</span>
              <span class="History__top-bar">
                <span :style="{ width: tpl.percent + '%' }" />
              </span>
            </div>
          </div>
          <p v-else class="History__ops-empty">{{ $t('dashboardNoTemplateRuns') }}</p>
        </section>

        <section class="History__ops-card">
          <span class="zp-overline">{{ $t('dashboardSignals') }}</span>
          <div class="History__signal-list">
            <div v-for="signal in sideSignals" :key="signal.key" class="History__side-signal">
              <span>{{ signal.label }}</span>
              <strong class="zp-tabular">{{ signal.value }}</strong>
            </div>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>

<style lang="scss">
.History {
  min-height: 100vh;
  min-width: 0;
  background: var(--zp-bg-page);
  color: var(--zp-text-primary);

  &__masthead {
    padding: var(--zp-space-lg) var(--zp-space-xl) var(--zp-space-md);
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-page);
  }

  &__topline {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--zp-space-lg);
  }

  &__title-block {
    display: flex;
    align-items: flex-start;
    gap: var(--zp-space-sm);
  }

  &__nav-toggle {
    margin-right: 0 !important;
  }

  &__title {
    font-family: var(--zp-font-display);
    font-size: var(--zp-text-xl);
    font-weight: 600;
    letter-spacing: var(--zp-tracking-display);
    line-height: var(--zp-leading-tight);
    color: var(--zp-text-primary);
    margin: 2px 0 0;
  }

  &__subtitle {
    margin: var(--zp-space-2xs) 0 0;
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-sm);
    line-height: var(--zp-leading-snug);
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--zp-space-sm);
    flex-wrap: wrap;
  }

  &__updated {
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-xs);
    white-space: nowrap;
  }

  &__action-btn,
  &__primary-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--zp-space-xs);
    height: 34px;
    padding: 0 var(--zp-space-sm);
    border-radius: var(--zp-radius-md);
    border: var(--zp-border-hair) solid var(--zp-border-default);
    background: var(--zp-bg-surface);
    color: var(--zp-text-primary);
    font-size: var(--zp-text-sm);
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
  }

  &__primary-action {
    border-color: var(--zp-brand);
    background: var(--zp-brand);
    color: var(--zp-brand-on) !important;
  }

  &__metrics {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--zp-space-sm);
    margin: var(--zp-space-lg) var(--zp-space-xl) 0;
  }

  &__metric {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr);
    gap: var(--zp-space-sm);
    min-height: 112px;
    padding: var(--zp-space-md);
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-surface);
  }

  &__metric-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: var(--zp-radius-md);
    background: var(--zp-status-neutral-bg);
    color: var(--zp-status-neutral);
  }

  &__metric--running &__metric-icon {
    background: var(--zp-status-running-bg);
    color: var(--zp-status-running);
  }

  &__metric--success &__metric-icon {
    background: var(--zp-status-success-bg);
    color: var(--zp-status-success);
  }

  &__metric--danger &__metric-icon {
    background: var(--zp-status-danger-bg);
    color: var(--zp-status-danger);
  }

  &__metric--neutral &__metric-icon {
    background: var(--zp-status-warning-bg);
    color: var(--zp-status-warning);
  }

  &__metric-body {
    min-width: 0;
  }

  &__metric-label {
    display: block;
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-2xs);
    font-weight: 700;
    letter-spacing: var(--zp-tracking-overline);
    line-height: 1;
    text-transform: uppercase;
  }

  &__metric-value {
    display: block;
    margin-top: var(--zp-space-xs);
    font-family: var(--zp-font-mono);
    font-size: var(--zp-text-3xl);
    font-weight: 500;
    color: var(--zp-text-primary);
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  &__metric-note {
    display: block;
    margin-top: var(--zp-space-xs);
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-xs);
    line-height: var(--zp-leading-snug);
  }

  &__menu-row {
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-page);
  }

  &__menu {
    .v-tabs {
      padding-left: var(--zp-space-xl) !important;
      background: transparent !important;
    }

    .v-tab {
      font-size: var(--zp-text-xs);
      font-weight: 700;
      letter-spacing: var(--zp-tracking-overline);
      text-transform: uppercase;
    }
  }

  &__readiness {
    display: grid;
    grid-template-columns: 180px repeat(4, minmax(0, 1fr));
    align-items: center;
    gap: var(--zp-space-md);
    margin: var(--zp-space-lg) var(--zp-space-xl) 0;
    padding: var(--zp-space-sm) var(--zp-space-md);
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-surface);
  }

  &__readiness-title {
    min-width: 0;
  }

  &__signal,
  &__side-signal {
    display: flex;
    align-items: center;
    min-width: 0;
    gap: var(--zp-space-xs);
  }

  &__signal-dot {
    width: 8px;
    height: 8px;
    border-radius: 1px;
    flex-shrink: 0;
    background: var(--zp-status-neutral);
  }

  &__signal-dot--success { background: var(--zp-status-success); }
  &__signal-dot--danger { background: var(--zp-status-danger); }
  &__signal-dot--warning { background: var(--zp-status-warning); }
  &__signal-dot--running { background: var(--zp-status-running); }

  &__signal-label {
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-xs);
    line-height: var(--zp-leading-snug);
    white-space: nowrap;
  }

  &__signal-value {
    margin-left: auto;
    color: var(--zp-text-primary);
    font-size: var(--zp-text-sm);
    white-space: nowrap;
  }

  &__workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 328px;
    gap: var(--zp-space-md);
    align-items: start;
    min-width: 0;
    padding: var(--zp-space-md) var(--zp-space-xl) var(--zp-space-2xl);
  }

  &__stream-panel,
  &__ops-card {
    min-width: 0;
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    background: var(--zp-bg-surface);
  }

  &__panel-head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--zp-space-md);
    padding: var(--zp-space-md);
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);

    h2 {
      margin: var(--zp-space-2xs) 0 0;
      color: var(--zp-text-primary);
      font-family: var(--zp-font-display);
      font-size: var(--zp-text-lg);
      font-weight: 650;
      line-height: var(--zp-leading-tight);
    }
  }

  &__panel-count {
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-xs);
    white-space: nowrap;
  }

  &__table-wrap {
    max-width: 100%;
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--zp-text-sm);
    color: var(--zp-text-primary);

    thead {
      background: var(--zp-bg-subtle);
      border-bottom: var(--zp-border-hair) solid var(--zp-border-default);
    }

    th {
      padding: var(--zp-space-sm) var(--zp-space-md);
      text-align: left;
      font-family: var(--zp-font-body);
      font-size: var(--zp-text-xs);
      font-weight: 600;
      letter-spacing: var(--zp-tracking-overline);
      text-transform: uppercase;
      color: var(--zp-text-tertiary);
      vertical-align: middle;
      white-space: nowrap;
    }

    td {
      padding: var(--zp-space-sm) var(--zp-space-md);
      border-top: var(--zp-border-hair) solid var(--zp-border-hairline);
      vertical-align: middle;
    }

    tbody tr:first-child td {
      border-top: none;
    }
  }

  &__row {
    cursor: pointer;
    transition: background-color var(--zp-duration-fast) var(--zp-ease-out);

    &:hover {
      background: var(--zp-bg-subtle);
    }
  }

  &__col-status { width: 92px; }
  &__col-id    { width: 56px; color: var(--zp-text-tertiary); }

  &__id-link {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: var(--zp-brand);
    }
  }

  &__col-version { width: 80px; }
  &__col-user  { width: 140px; }
  &__col-time  { width: 180px; white-space: nowrap; color: var(--zp-text-secondary); }
  &__col-duration {
    width: 132px;
    text-align: right;
    color: var(--zp-text-secondary);
  }

  &__duration-bar {
    display: block;
    width: 52px;
    height: 3px;
    margin: 5px 0 0 auto;
    background: var(--zp-bg-muted);
    border-radius: var(--zp-radius-sm);
    overflow: hidden;

    span {
      display: block;
      height: 100%;
      background: var(--zp-brand);
    }
  }

  &__template {
    display: flex;
    align-items: center;
    gap: var(--zp-space-xs);
  }

  &__app-icon {
    color: var(--zp-text-secondary) !important;
    flex-shrink: 0;
  }

  &__template-link {
    color: var(--zp-text-primary);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      color: var(--zp-brand);
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  &__sub {
    display: flex;
    align-items: center;
    gap: var(--zp-space-2xs);
    font-size: var(--zp-text-xs);
    color: var(--zp-text-tertiary);
    margin-top: 3px;
    margin-left: 22px; // align under template-link, past the icon
    max-width: 60ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__sub-icon {
    color: var(--zp-text-tertiary) !important;
    flex-shrink: 0;
  }

  &__user {
    font-family: var(--zp-font-body);
    color: var(--zp-text-secondary);
  }

  &__muted {
    color: var(--zp-text-tertiary);
    font-family: var(--zp-font-mono);
  }

  &__empty {
    padding: var(--zp-space-3xl) var(--zp-space-xl);
    text-align: center;
    color: var(--zp-text-tertiary);

    p {
      margin-top: var(--zp-space-xs);
      font-size: var(--zp-text-sm);
    }
  }

  &__pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--zp-space-md);
    padding: var(--zp-space-sm) var(--zp-space-md);
    border-top: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-subtle);
  }

  &__pager-range {
    color: var(--zp-text-tertiary);
  }

  &__pager-actions {
    display: flex;
    align-items: center;
    gap: var(--zp-space-2xs);
  }

  &__pager-pos {
    padding: 0 var(--zp-space-sm);
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-sm);
  }

  &__pager-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 var(--zp-space-2xs);
    background: var(--zp-bg-surface);
    color: var(--zp-text-primary);
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-sm);
    font-family: var(--zp-font-mono);
    font-size: var(--zp-text-sm);
    line-height: 1;
    cursor: pointer;
    transition: background-color var(--zp-duration-fast) var(--zp-ease-out);

    &:hover:not(:disabled) {
      background: var(--zp-bg-muted);
      border-color: var(--zp-border-default);
    }

    &:disabled {
      color: var(--zp-text-disabled);
      cursor: not-allowed;
    }
  }

  &__ops {
    display: grid;
    gap: var(--zp-space-sm);
  }

  &__ops-card {
    padding: var(--zp-space-md);
  }

  &__ops-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--zp-space-sm);
  }

  &__ops-window {
    color: var(--zp-text-tertiary);
    font-size: var(--zp-text-xs);
    white-space: nowrap;
  }

  &__ops-open {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: var(--zp-space-sm);
    margin-top: var(--zp-space-sm);
    padding: 0;
    border: none;
    background: transparent;
    color: var(--zp-text-primary);
    text-align: left;
    cursor: pointer;

    strong,
    small {
      display: block;
    }

    strong {
      font-size: var(--zp-text-sm);
      line-height: var(--zp-leading-snug);
    }

    small {
      margin-top: var(--zp-space-2xs);
      color: var(--zp-text-tertiary);
      font-size: var(--zp-text-xs);
    }
  }

  &__ops-message,
  &__ops-empty {
    margin: var(--zp-space-sm) 0 0;
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-xs);
    line-height: var(--zp-leading-normal);
  }

  &__ops-card--danger {
    border-color: var(--zp-status-danger-bg);
  }

  &__ops-card--success {
    border-color: var(--zp-status-success-bg);
  }

  &__top-list {
    display: grid;
    gap: var(--zp-space-sm);
    margin-top: var(--zp-space-sm);
  }

  &__top-item {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 34px;
    gap: var(--zp-space-xs);
    align-items: center;
  }

  &__top-name {
    min-width: 0;
    color: var(--zp-text-primary);
    font-size: var(--zp-text-xs);
    font-weight: 650;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__top-count {
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-xs);
    text-align: right;
  }

  &__top-bar {
    grid-column: 1 / -1;
    height: 3px;
    background: var(--zp-bg-muted);
    border-radius: var(--zp-radius-sm);
    overflow: hidden;

    span {
      display: block;
      height: 100%;
      background: var(--zp-brand);
    }
  }

  &__signal-list {
    display: grid;
    gap: var(--zp-space-sm);
    margin-top: var(--zp-space-sm);
  }

  &__side-signal {
    justify-content: space-between;
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-xs);

    strong {
      color: var(--zp-text-primary);
      font-weight: 600;
    }
  }
}

@media (max-width: 1263px) {
  .History {
    &__metrics {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__readiness {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__workspace {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 700px) {
  .History {
    &__masthead {
      padding: var(--zp-space-md);
    }

    &__topline,
    &__actions {
      align-items: stretch;
      flex-direction: column;
    }

    &__actions {
      width: 100%;
    }

    &__updated {
      white-space: normal;
    }

    &__metrics,
    &__readiness {
      grid-template-columns: 1fr;
    }

    &__metric {
      min-height: 92px;
    }

    &__menu {
      .v-tabs {
        padding-left: 0 !important;
      }
    }

    &__metrics,
    &__readiness,
    &__workspace {
      margin-left: var(--zp-space-md);
      margin-right: var(--zp-space-md);
    }

    &__workspace {
      padding-left: 0;
      padding-right: 0;
    }

    &__table {
      min-width: 920px;
    }
  }
}
</style>

<script>
import ItemListPageBase from '@/components/ItemListPageBase';
import EventBus from '@/event-bus';
import TaskStatus from '@/components/TaskStatus.vue';
import TaskLink from '@/components/TaskLink.vue';
import socket from '@/socket';
import { TEMPLATE_TYPE_ICONS } from '@/lib/constants';
import AppsMixin from '@/components/AppsMixin';
import DashboardMenu from '@/components/DashboardMenu.vue';

// labelKey resolved via $t in the computed; this lets one source of
// status copy live in the lang files instead of being duplicated here.
const SUMMARY_BUCKETS = [
  {
    key: 'running',
    labelKey: 'statusRunning',
    variant: 'running',
    statuses: ['running', 'starting'],
  },
  {
    key: 'success',
    labelKey: 'statusOk',
    variant: 'success',
    statuses: ['success', 'confirmed'],
  },
  {
    key: 'failed',
    labelKey: 'statusFailed',
    variant: 'danger',
    statuses: ['error', 'rejected'],
  },
  {
    key: 'queued',
    labelKey: 'statusQueued',
    variant: 'neutral',
    statuses: ['waiting', 'waiting_confirmation'],
  },
  {
    key: 'stopped',
    labelKey: 'statusStopped',
    variant: 'neutral',
    statuses: ['stopping', 'stopped'],
  },
];

const METRIC_META = {
  running: { icon: 'play', noteKey: 'dashboardMetricRunningNote' },
  success: { icon: 'check', noteKey: 'dashboardMetricSuccessNote' },
  failed: { icon: 'square', noteKey: 'dashboardMetricFailedNote' },
  queued: { icon: 'pause', noteKey: 'dashboardMetricQueuedNote' },
};

export default {
  mixins: [ItemListPageBase, AppsMixin],

  data() {
    return {
      TEMPLATE_TYPE_ICONS,
      page: 1,
      pageSize: 20,
    };
  },

  components: { DashboardMenu, TaskStatus, TaskLink },

  computed: {
    pageCount() {
      const total = (this.items || []).length;
      return Math.max(1, Math.ceil(total / this.pageSize));
    },

    visibleItems() {
      const items = this.items || [];
      const start = (this.page - 1) * this.pageSize;
      return items.slice(start, start + this.pageSize);
    },

    pageRangeStart() {
      return this.items?.length ? (this.page - 1) * this.pageSize + 1 : 0;
    },

    pageRangeEnd() {
      return Math.min((this.items || []).length, this.page * this.pageSize);
    },

    statusBuckets() {
      const counts = (this.items || []).reduce((acc, item) => {
        acc[item.status] = (acc[item.status] || 0) + 1;
        return acc;
      }, {});

      return SUMMARY_BUCKETS.map((bucket) => ({
        ...bucket,
        label: this.$t(bucket.labelKey),
        count: bucket.statuses.reduce((sum, s) => sum + (counts[s] || 0), 0),
      }));
    },

    metricCards() {
      return this.statusBuckets
        .filter((bucket) => bucket.key !== 'stopped')
        .map((bucket) => ({
          ...bucket,
          icon: (METRIC_META[bucket.key] || {}).icon || 'circle',
          note: this.$t((METRIC_META[bucket.key] || {}).noteKey || 'dashboardMetricDefaultNote', {
            total: this.totalTasks,
            percent: this.percentOfTotal(bucket.count),
          }),
        }));
    },

    totalTasks() {
      return (this.items || []).length;
    },

    latestTask() {
      return (this.items || [])[0] || null;
    },

    activeCount() {
      return this.countStatuses(['running', 'starting']);
    },

    queuedCount() {
      return this.countStatuses(['waiting', 'waiting_confirmation']);
    },

    successCount() {
      return this.countStatuses(['success', 'confirmed']);
    },

    failureCount() {
      return this.countStatuses(['error', 'rejected']);
    },

    successRate() {
      const resolved = this.successCount + this.failureCount;
      return resolved === 0 ? 0 : Math.round((this.successCount / resolved) * 100);
    },

    lastFailure() {
      return (this.items || []).find((item) => ['error', 'rejected'].includes(item.status)) || null;
    },

    lastSuccess() {
      return (this.items || []).find((item) => ['success', 'confirmed'].includes(item.status)) || null;
    },

    maxDurationMs() {
      const values = (this.visibleItems || [])
        .map((item) => this.durationMs(item))
        .filter((value) => value > 0);
      return values.length > 0 ? Math.max(...values) : 1;
    },

    topTemplates() {
      const counts = (this.items || []).reduce((acc, item) => {
        const key = item.template_id || item.tpl_alias || 'unknown';
        if (!acc[key]) {
          acc[key] = {
            key,
            name: item.tpl_alias || this.$t('template'),
            count: 0,
          };
        }
        acc[key].count += 1;
        return acc;
      }, {});

      const list = Object.values(counts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      const max = list[0]?.count || 1;

      return list.map((item) => ({
        ...item,
        percent: Math.max(8, Math.round((item.count / max) * 100)),
      }));
    },

    readinessSignals() {
      return [
        {
          key: 'history',
          label: this.$t('dashboardSignalHistory'),
          value: this.totalTasks,
          variant: this.totalTasks > 0 ? 'success' : 'warning',
        },
        {
          key: 'active',
          label: this.$t('dashboardSignalActive'),
          value: this.activeCount,
          variant: this.activeCount > 0 ? 'running' : 'success',
        },
        {
          key: 'queue',
          label: this.$t('dashboardSignalQueue'),
          value: this.queuedCount,
          variant: this.queuedCount > 0 ? 'warning' : 'success',
        },
        {
          key: 'success-rate',
          label: this.$t('dashboardSignalSuccessRate'),
          value: `${this.successRate}%`,
          variant: this.successRate >= 80 || this.totalTasks === 0 ? 'success' : 'danger',
        },
      ];
    },

    sideSignals() {
      return [
        {
          key: 'operators',
          label: this.$t('dashboardSignalOperators'),
          value: this.uniqueUsers,
        },
        {
          key: 'templates',
          label: this.$t('dashboardSignalTemplates'),
          value: this.topTemplates.length,
        },
        {
          key: 'failures',
          label: this.$t('dashboardSignalFailures'),
          value: this.failureCount,
        },
        {
          key: 'latest',
          label: this.$t('dashboardSignalLatest'),
          value: this.latestTask ? this.formatId(this.latestTask.id) : '—',
        },
      ];
    },

    uniqueUsers() {
      const users = new Set((this.items || [])
        .map((item) => item.user_name)
        .filter(Boolean));
      return users.size;
    },
  },

  watch: {
    async projectId() {
      await this.loadItems();
      this.page = 1;
    },

    pageCount(newCount) {
      // After loadItems trims the list (e.g. project switch), clamp the
      // current page so we never sit on an empty slice.
      if (this.page > newCount) {
        this.page = newCount;
      }
    },
  },

  created() {
    socket.addListener((data) => this.onWebsocketDataReceived(data));
  },

  methods: {
    formatId(id) {
      if (id == null) return '—';
      return `${id}`.padStart(4, '0');
    },

    countStatuses(statuses) {
      return (this.items || []).filter((item) => statuses.includes(item.status)).length;
    },

    percentOfTotal(value) {
      return this.totalTasks === 0 ? 0 : Math.round((value / this.totalTasks) * 100);
    },

    durationMs(item) {
      if (!item || !item.start) return 0;
      const start = new Date(item.start).getTime();
      const end = item.end ? new Date(item.end).getTime() : Date.now();
      if (Number.isNaN(start) || Number.isNaN(end)) return 0;
      return Math.max(0, end - start);
    },

    durationPercent(item) {
      if (this.maxDurationMs <= 0) return 0;
      const share = (this.durationMs(item) / this.maxDurationMs) * 100;
      return Math.max(5, Math.min(100, Math.round(share)));
    },

    // Build a route descriptor that adds ?t=<id> on top of the current
    // query. App.vue's $route watcher reacts to `t` and emits i-show-task,
    // which means router-link middle-click and same-tab navigation share
    // a single code path.
    taskRoute(taskId) {
      return {
        path: this.$route.path,
        query: { ...this.$route.query, t: taskId },
      };
    },

    openTask(taskId) {
      const target = this.taskRoute(taskId);
      // Avoid throwing NavigationDuplicated when the same task is already
      // open in the dialog.
      if (this.$route.query.t === String(taskId)) {
        EventBus.$emit('i-show-task', { taskId });
        return;
      }
      this.$router.push(target).catch(() => { /* ignore aborted nav */ });
    },

    showTaskLog(taskId) {
      this.openTask(taskId);
    },

    async onWebsocketDataReceived(data) {
      if (data.project_id !== this.projectId || data.type !== 'update') {
        return;
      }

      if (!this.items.some((item) => item.id === data.task_id)) {
        await this.loadItems();
      }

      const task = this.items.find((item) => item.id === data.task_id);

      if (task) {
        Object.assign(task, {
          ...data,
          type: undefined,
        });
      }
    },

    getHeaders() {
      // Returned for the legacy ItemListPageBase API; the template renders its
      // own header row using these column slots directly.
      return [
        { text: this.$i18n.t('task2'), value: 'tpl_alias', sortable: false },
        { text: this.$i18n.t('version'), value: 'version', sortable: false },
        { text: this.$i18n.t('status'), value: 'status', sortable: false },
        { text: this.$i18n.t('user'), value: 'user_name', sortable: false },
        { text: this.$i18n.t('start'), value: 'start', sortable: false },
        { text: this.$i18n.t('duration'), value: 'end', sortable: false },
      ];
    },

    getItemsUrl() {
      return `/api/project/${this.projectId}/tasks/last`;
    },
  },
};
</script>
